/**
 * @file FileLink.tsx
 * @description 「已上傳檔案」的統一展示元件。
 *
 * 渲染：[檔案圖示][可點擊的檔名][大小][狀態文字][刪除按鈕?]。
 * 點擊檔名會在新分頁預覽（透過 lib/file-preview.ts 的 previewFile）。
 *
 * 設計目標：
 * - 全站所有「上傳成功後展示給使用者的那一行」共用同一視覺與互動，
 *   避免有些地方能點預覽、有些地方只是純文字。
 * - 上傳進行中（status='uploading'）時，檔名不可點擊；上傳失敗（status='error'）
 *   時改為紅色文字並顯示錯誤訊息。
 * - 表格欄位場景透過 `compact` 隱藏大小與狀態文字，只保留可點擊檔名。
 */

import { Button, Space, Typography } from 'antd';
import { FileText, X } from 'lucide-react';

import { previewFile } from '@/lib/file-preview';
import { resolveFileBlobUrl } from '@/pwa/file-blob-url';

/** FileLink 元件 props */
export interface FileLinkProps {
  /**
   * 資料庫 files.id。沒有 id 時（例如還在上傳中）檔名顯示為純文字、不可點擊。
   */
  fileId?: number | null;
  /** 顯示的原始檔名 */
  filename: string;
  /** 檔案大小（位元組）。compact 模式下不顯示 */
  sizeBytes?: number;
  /** 上傳狀態。預設 'done'（已落庫，可預覽） */
  status?: 'uploading' | 'done' | 'error';
  /** uploading 狀態下的進度百分比（0-100） */
  progress?: number;
  /** error 狀態下的錯誤訊息 */
  error?: string;
  /**
   * 提供時顯示「✕」刪除按鈕，點擊呼叫此 callback。
   * uploading 狀態下按鈕會被禁用。
   */
  onRemove?: () => void;
  /**
   * 表格欄位等空間有限的場景使用，會：
   * - 不顯示檔案大小
   * - 不顯示「已上傳」綠字（done 狀態下）
   */
  compact?: boolean;
}

/**
 * 將位元組數格式化為人類可讀字串（B / KB / MB）。
 *
 * 與 dispatch/new.tsx 內原本的 formatBytes 等價，這裡內聚到本元件以便獨立使用。
 */
function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * 「已上傳檔案」一行的標準展示。
 *
 * 點擊檔名 → 新分頁預覽（PDF / 圖片瀏覽器原生顯示；Office 文件經後端 LibreOffice
 * 轉 PDF 後同樣原生顯示，見 `lib/file-preview.ts`）。
 */
export function FileLink({
  fileId,
  filename,
  sizeBytes,
  status = 'done',
  progress,
  error,
  onRemove,
  compact = false,
}: FileLinkProps) {
  const iconColor = status === 'error' ? '#ef4444' : '#64748b';

  // 是否可點擊預覽：必須是 done 狀態且具有 fileId
  const canPreview = status === 'done' && typeof fileId === 'number';

  // 點擊預覽：交給 lib/file-preview.ts 處理 URL 取得 + 開新分頁
  const handlePreview = async () => {
    if (!canPreview) return;
    const localUrl = await resolveFileBlobUrl(fileId);
    if (localUrl) {
      // PWA 離線快取命中：blob: URL 只在本機有效，無法送去後端轉檔，
      // Office 文件離線場景仍維持原生下載行為，不強制轉 PDF。
      window.open(localUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    void previewFile(fileId, filename);
  };

  // 檔名節點：可預覽時為 <a>，否則為純文字
  const filenameNode = canPreview ? (
    <a
      onClick={handlePreview}
      style={{ fontWeight: 500, cursor: 'pointer' }}
      // antd <a> 預設藍色；使用者一眼能識別這是可點擊的
    >
      {filename}
    </a>
  ) : (
    <span style={{ fontWeight: 500, color: status === 'error' ? '#ef4444' : undefined }}>
      {filename}
    </span>
  );

  return (
    <Space size={8} align="center" style={{ display: 'inline-flex', maxWidth: '100%' }}>
      <FileText size={14} color={iconColor} style={{ flexShrink: 0 }} />
      {filenameNode}
      {!compact && typeof sizeBytes === 'number' && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {formatBytes(sizeBytes)}
        </Typography.Text>
      )}
      {status === 'uploading' && (
        <Typography.Text style={{ color: '#3b82f6', fontSize: 12 }}>
          上傳中 {Math.round(progress ?? 0)}%
        </Typography.Text>
      )}
      {!compact && status === 'done' && (
        <Typography.Text style={{ color: '#16a34a', fontSize: 12 }}>已上傳</Typography.Text>
      )}
      {status === 'error' && (
        <Typography.Text style={{ color: '#ef4444', fontSize: 12 }}>
          {error ?? '上傳失敗'}
        </Typography.Text>
      )}
      {onRemove && (
        <Button
          type="text"
          danger
          size="small"
          icon={<X size={14} />}
          disabled={status === 'uploading'}
          onClick={onRemove}
          aria-label="移除附件"
        />
      )}
    </Space>
  );
}
